/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { HighContrastSelector } from '@fluentui/style-utilities';
import { Range, createSliderWithTooltip } from 'rc-slider';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getStylistV2, important, joinClassNames } from 'src/core/themes/luisStylist';
import { FilterScore, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type FiltersBladeSliderProps = {
    /**
     * Score of the intents
     */
    score: FilterScore;

    /**
     * Fires when range values changed
     */
    onScoreChange: (change: SelectionChange) => void;

    /**
     * Disable changing the max in the score.
     */
    disableMaxChange?: boolean;
};

const { getClassName, styleComponent } = getStylistV2('FiltersBladeSlider');

/**
 * Due to the incomplete typings from the DefinitelyTyped
 * The following castings allows the developers to add the missing props.
 */
const RangeSlider = createSliderWithTooltip(Range);

const sliderRangeClassName = getClassName('SliderRange', { display: 'none' });
const SliderContainer = styleComponent<IStackProps>(Stack)('SliderContainer', theme => ({ padding: theme.sliderPadding, marginRight: 8 }));
const disabledMaxSliderClassName = getClassName('disabledMaxSliderClassName', { $nest: { '.rc-slider-handle-2': { display: 'none' } } });
const sliderTrackClassName = getClassName('SliderTrack', theme => ({
    $nest: {
        '.rc-slider-track': {
            backgroundColor: theme.sliderColor,
            $nest: { [HighContrastSelector]: { border: '1px solid' } }
        },
        '.rc-slider-rail': {
            $nest: { [HighContrastSelector]: { border: '1px dashed' } }
        },
        '.rc-slider-handle': {
            border: theme.scoreSliderHandles,
            $nest: {
                '&:hover, &:active, &:focus': important({
                    boxShadow: 'none',
                    borderColor: 'unset',
                    border: theme.scoreSliderHandles
                }),
                '&:focus': {
                    borderWidth: '4px !important',
                    $nest: { [HighContrastSelector]: important({ border: '7px solid' }) }
                }
            }
        }
    }
}));

/**
 * Represents the range slider component
 */
export const FiltersBladeSlider = (props: FiltersBladeSliderProps & { minHandleAriaLabel?: string; maxHandleAriaLabel?: string }) => {
    const {
        score,
        onScoreChange,
        disableMaxChange = false,
        minHandleAriaLabel = 'range minimum',
        maxHandleAriaLabel = 'range maximum'
    } = props;
    const [internalScore, setInternalScore] = React.useState(score);

    React.useEffect(() => {
        setInternalScore(score);
    }, [score.max, score.min]);

    const getLabelText = () => {
        if (disableMaxChange) {
            return `${internalScore.min}`;
        }
        return `${internalScore.min}-${internalScore.max}`;
    };

    return (
        <Stack horizontal verticalAlign="center">
            <SliderContainer grow>
                <RangeSlider
                    min={0}
                    max={1}
                    step={0.01}
                    data-automation-id="filtersRangeSlider"
                    value={[internalScore.min, internalScore.max]}
                    tipProps={{ visible: false, overlay: sliderRangeClassName }}
                    ariaLabelGroupForHandles={[minHandleAriaLabel, maxHandleAriaLabel]}
                    onAfterChange={() => onScoreChange({ changeType: 'score', args: internalScore })}
                    className={joinClassNames(sliderTrackClassName, disableMaxChange && disabledMaxSliderClassName)}
                    onChange={(values: number[]) => setInternalScore({ min: values[0], max: disableMaxChange ? score.max : values[1] })}
                />
            </SliderContainer>
            <LuisText>{getLabelText()}</LuisText>
        </Stack>
    );
};
